import api from '@/lib/axios'

export interface Project {
  id: string
  name: string
  description?: string
  status: 'PLANNING' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED'
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  startDate?: string
  dueDate?: string
  completedAt?: string
  progress: number
  color?: string
  ownerId: string
  owner: { id: string; firstName: string; lastName: string; avatarUrl?: string }
  members: ProjectMember[]
  _count: { tasks: number; members: number }
  createdAt: string
}

export interface ProjectMember {
  id: string
  userId: string
  role: 'OWNER' | 'MANAGER' | 'MEMBER' | 'VIEWER'
  user: { id: string; firstName: string; lastName: string; avatarUrl?: string }
}

export interface TaskStatus {
  id: string
  name: string
  color: string
  position: number
  isDone: boolean
}

export interface Task {
  id: string
  title: string
  description?: string
  statusId: string
  status?: TaskStatus
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  position: number
  projectId: string
  sprintId?: string
  assigneeId?: string
  assignee?: { id: string; firstName: string; lastName: string; avatarUrl?: string }
  labels: string[]
  dueDate?: string
  estimatedHours?: number
  completedAt?: string
  createdAt: string
  _count?: { comments: number; attachments: number; timeLogs: number }
}

export interface Sprint {
  id: string
  name: string
  goal?: string
  status: 'PLANNED' | 'ACTIVE' | 'COMPLETED'
  startDate?: string
  endDate?: string
  projectId: string
  _count?: { tasks: number }
}

export interface TimeLog {
  id: string
  taskId: string
  userId: string
  user?: { id: string; firstName: string; lastName: string }
  hours: number
  description?: string
  logDate: string
}

export interface ProjectLabel {
  id: string
  name: string
  color: string
  projectId: string
}

export const projectsApi = {
  // ── Projets ────────────────────────────────────────────────────────────────
  list: (params?: { search?: string; status?: string; page?: number; limit?: number }) =>
    api.get('/projects', { params }).then(r => r.data?.data ?? r.data),

  get: (id: string) =>
    api.get(`/projects/${id}`).then(r => r.data.data),

  create: (data: { name: string; description?: string; color?: string; priority?: string; startDate?: string; dueDate?: string }) =>
    api.post('/projects', data).then(r => r.data.data),

  update: (id: string, data: Partial<{ name: string; description: string; status: string; priority: string; color: string; startDate: string; dueDate: string }>) =>
    api.patch(`/projects/${id}`, data).then(r => r.data.data),

  delete: (id: string) =>
    api.delete(`/projects/${id}`).then(r => r.data.data),

  getStats: (id: string) =>
    api.get(`/projects/${id}/stats`).then(r => r.data.data),

  // ── Membres ────────────────────────────────────────────────────────────────
  addMember: (projectId: string, data: { userId: string; role: string }) =>
    api.post(`/projects/${projectId}/members`, data).then(r => r.data.data),

  removeMember: (projectId: string, memberId: string) =>
    api.delete(`/projects/${projectId}/members/${memberId}`).then(r => r.data.data),

  // ── Statuts Kanban ─────────────────────────────────────────────────────────
  getStatuses: (projectId: string) =>
    api.get(`/projects/${projectId}/statuses`).then(r => r.data.data),

  createStatus: (projectId: string, data: { name: string; color: string; isDone?: boolean }) =>
    api.post(`/projects/${projectId}/statuses`, data).then(r => r.data.data),

  updateStatus: (projectId: string, statusId: string, data: { name?: string; color?: string }) =>
    api.patch(`/projects/${projectId}/statuses/${statusId}`, data).then(r => r.data.data),

  // ── Tâches ─────────────────────────────────────────────────────────────────
  getTasks: (projectId: string, params?: { sprintId?: string; statusId?: string; assigneeId?: string }) =>
    api.get(`/projects/${projectId}/tasks`, { params }).then(r => r.data.data),

  getTask: (projectId: string, taskId: string) =>
    api.get(`/projects/${projectId}/tasks/${taskId}`).then(r => r.data.data),

  createTask: (projectId: string, data: { title: string; statusId: string; priority?: string; assigneeId?: string; dueDate?: string; estimatedHours?: number; sprintId?: string }) =>
    api.post(`/projects/${projectId}/tasks`, data).then(r => r.data.data),

  updateTask: (projectId: string, taskId: string, data: Partial<{ title: string; description: string; statusId: string; priority: string; assigneeId: string; dueDate: string; estimatedHours: number; labels: string[] }>) =>
    api.patch(`/projects/${projectId}/tasks/${taskId}`, data).then(r => r.data.data),

  moveTask: (projectId: string, taskId: string, data: { statusId: string; position: number }) =>
    api.patch(`/projects/${projectId}/tasks/${taskId}/move`, data).then(r => r.data.data),

  deleteTask: (projectId: string, taskId: string) =>
    api.delete(`/projects/${projectId}/tasks/${taskId}`).then(r => r.data.data),

  getTaskComments: (projectId: string, taskId: string) =>
    api.get(`/projects/${projectId}/tasks/${taskId}/comments`).then(r => r.data.data),

  addTaskComment: (projectId: string, taskId: string, content: string) =>
    api.post(`/projects/${projectId}/tasks/${taskId}/comments`, { content }).then(r => r.data.data),

  // ── Sprints ────────────────────────────────────────────────────────────────
  getSprints: (projectId: string) =>
    api.get(`/projects/${projectId}/sprints`).then(r => r.data.data),

  createSprint: (projectId: string, data: { name: string; goal?: string; startDate?: string; endDate?: string }) =>
    api.post(`/projects/${projectId}/sprints`, data).then(r => r.data.data),

  startSprint: (projectId: string, sprintId: string) =>
    api.patch(`/projects/${projectId}/sprints/${sprintId}/start`).then(r => r.data.data),

  closeSprint: (projectId: string, sprintId: string) =>
    api.patch(`/projects/${projectId}/sprints/${sprintId}/close`).then(r => r.data.data),

  // ── Labels ─────────────────────────────────────────────────────────────────
  getLabels: (projectId: string) =>
    api.get(`/projects/${projectId}/labels`).then(r => r.data.data),

  createLabel: (projectId: string, data: { name: string; color: string }) =>
    api.post(`/projects/${projectId}/labels`, data).then(r => r.data.data),

  deleteLabel: (projectId: string, labelId: string) =>
    api.delete(`/projects/${projectId}/labels/${labelId}`).then(r => r.data.data),

  // ── Gantt — Bloc C ─────────────────────────────────────────────────────────
  getGantt: (projectId: string) =>
    api.get(`/projects/${projectId}/gantt`).then(r => r.data.data ?? r.data),

  // ── Sprints — Burndown ─────────────────────────────────────────────────────
  getBurndown: (projectId: string, sprintId: string) =>
    api.get(`/projects/${projectId}/sprints/${sprintId}/burndown`).then(r => r.data.data),

  // ── Time Logs ──────────────────────────────────────────────────────────────
  getTimeLogs: (projectId: string, taskId: string) =>
    api.get(`/projects/${projectId}/tasks/${taskId}/timelogs`).then(r => r.data.data),

  addTimeLog: (projectId: string, taskId: string, data: { minutes: number; description?: string; date: string }) =>
    api.post(`/projects/${projectId}/tasks/${taskId}/timelogs`, data).then(r => r.data.data),

  deleteTimeLog: (projectId: string, logId: string) =>
    api.delete(`/projects/${projectId}/timelogs/${logId}`).then(r => r.data.data),

  // ── Export (format FDV ORoyal — Sprint 1 NexusCorp) ──────────────────────────
  exportData: (projectId: string) =>
    api.get(`/projects/${projectId}/export`).then(r => r.data.data ?? r.data),
}
